import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { AppProvider } from './context/AppContext';
import { GlobalStyles } from './styles/GlobalStyles';
import { theme } from './styles/theme';
import { useAuth } from './context/AuthContext';
import AuthLayout from './components/Layout/AuthLayout';
import AppLayout from './components/Layout/AppLayout';
import LoginScreen from './components/Auth/LoginScreen';
import VoiceEnrollment from './components/VoiceEnrollment/VoiceEnrollment';
import OAuthCallback from './components/Connections/OAuthCallback';
import { voiceSkipService } from './services/voiceSkipService';

const AppContent = () => {
  const { user, loading, isAuthenticated } = useAuth();
  const [needsVoiceSetup, setNeedsVoiceSetup] = React.useState(false);
  const [isExistingUser, setIsExistingUser] = React.useState(false);

  React.useEffect(() => {
    if (isAuthenticated && user) {
      const userId = user.uid || user.id;
      const token = localStorage.getItem('sarvik_token');
      
      // Check if this is a developer bypass user
      const isDeveloperBypass = userId === 'dev-user-123' || user.email === 'developer@sarvik.com';
      
      if (isDeveloperBypass) {
        // Developer bypass - skip voice enrollment completely
        setNeedsVoiceSetup(false);
        return;
      }
      
      // Check if user should see voice enrollment using the server-side skip service
      if (token) {
        checkVoiceEnrollmentStatus(token, userId);
      } else {
        // No token, show voice enrollment for new users
        setIsExistingUser(false);
        setNeedsVoiceSetup(true);
      }
    } else {
      // User logged out - clear session markers
      if (user) {
        const userId = user.uid || user.id;
        sessionStorage.removeItem(`current_session_${userId}`);
      }
    }
  }, [isAuthenticated, user]);

  const checkVoiceEnrollmentStatus = async (token, userId) => {
    try {
      // Check if user's voice profile is completed on server-side
      const isVoiceProfileCompleted = await voiceSkipService.isVoiceProfileCompleted(token);
      
      if (isVoiceProfileCompleted) {
        // Existing user with voice profile completed on server
        // Check if we should show the update prompt
        const currentSessionKey = `voice_prompt_shown_${userId}_${new Date().toDateString()}`;
        const promptShownToday = sessionStorage.getItem(currentSessionKey);
        
        // Also check if this is a fresh login (not a page refresh)
        const isNewLogin = !sessionStorage.getItem(`current_session_${userId}`);
        
        if (isNewLogin && !promptShownToday) {
          // This is a new login and haven't shown prompt today
          sessionStorage.setItem(currentSessionKey, 'true');
          sessionStorage.setItem(`current_session_${userId}`, 'active');
          setIsExistingUser(true);
          setNeedsVoiceSetup(true);
        } else {
          // Already shown prompt or this is just a refresh
          sessionStorage.setItem(`current_session_${userId}`, 'active');
          setNeedsVoiceSetup(false);
        }
      } else {
        // Check if user should see voice enrollment
        const shouldShow = await voiceSkipService.shouldShowVoiceEnrollment(token);
        
        if (!shouldShow) {
          // Don't show voice enrollment
          setNeedsVoiceSetup(false);
        } else {
          // New user or user without completed voice profile
          setIsExistingUser(false);
          setNeedsVoiceSetup(true);
        }
      }
    } catch (error) {
      console.error('Error checking voice enrollment status:', error);
      // On error, show voice enrollment for safety
      setIsExistingUser(false);
      setNeedsVoiceSetup(true);
    }
  };

  if (loading) {
    return null;
  }

  if (!isAuthenticated) {
    return (
      <AuthLayout>
        <LoginScreen />
      </AuthLayout>
    );
  }

  // Check if developer bypass
  const isDeveloperBypass = user?.uid === 'dev-user-123' || user?.email === 'developer@sarvik.com';

  if (needsVoiceSetup && !isDeveloperBypass) {
    return <VoiceEnrollment isExistingUser={isExistingUser} />;
  }

  return (
    <AppProvider>
      <AppLayout />
    </AppProvider>
  );
};

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <Router>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<AppContent />} />
            <Route path="/voice-setup" element={<VoiceEnrollment />} />
            <Route path="/oauth/callback" element={<OAuthCallback />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </Router>
      
      <Toaster
        position="top-right"
        containerStyle={{
          top: 60, // Move down to avoid overlapping with title bar (minimize, maximize, close buttons)
          right: 20,
        }}
        toastOptions={{
          duration: 4000,
          style: {
            background: theme.colors.dark.elevated,
            color: theme.colors.dark.text,
            border: `1px solid ${theme.colors.dark.border}`,
            borderRadius: theme.borderRadius.md,
          },
        }}
      />
    </ThemeProvider>
  );
};

export default App;